/**
 * AdjustDegreeOfSuccess (Foundry: rule-element/adjust-degree-of-success.ts): shift or fix the
 * degree of success of a selector's rolls. Recorded on the statistics for #19 to apply.
 */
import { resolveInjectedProperties } from "../resolve";
import type { EngineState } from "../state";
import type { DegreeAdjustment, DegreeAdjustmentAmount } from "../synthetics";
import { DEGREE_OF_SUCCESS_STRINGS, type DegreeOfSuccessString } from "../values";
import type { RuleHandler, RuleInstance } from "./base";

const AMOUNTS: Record<string, DegreeAdjustmentAmount> = {
  "two-degrees-better": 2,
  "one-degree-better": 1,
  "one-degree-worse": -1,
  "two-degrees-worse": -2,
  "to-critical-failure": "criticalFailure",
  "to-failure": "failure",
  "to-success": "success",
  "to-critical-success": "criticalSuccess",
};

interface AdjustDegreeData {
  selector: string;
  adjustment: Partial<Record<"all" | DegreeOfSuccessString, string>>;
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  if (typeof s.selector !== "string" || !s.selector) return rule.fail("selector: must be a non-empty string");
  const raw = s.adjustment ?? s.adjusment;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return rule.fail("adjustment: must be an object");
  const adjustment: AdjustDegreeData["adjustment"] = {};
  for (const [outcome, amount] of Object.entries(raw as Record<string, unknown>)) {
    if (outcome !== "all" && !(DEGREE_OF_SUCCESS_STRINGS as readonly string[]).includes(outcome)) return rule.fail(`adjustment: "${outcome}" is not a degree of success`);
    if (typeof amount !== "string" || !(amount in AMOUNTS)) return rule.fail(`adjustment: "${String(amount)}" is not a degree adjustment`);
    adjustment[outcome as "all" | DegreeOfSuccessString] = amount;
  }
  rule.data = { selector: s.selector, adjustment } as unknown as Record<string, unknown>;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as AdjustDegreeData;
  const selector = resolveInjectedProperties(rule, state, data.selector);
  if (rule.ignored) return;
  const adjustments: Partial<Record<"all" | DegreeOfSuccessString, DegreeAdjustment>> = {};
  for (const outcome of ["all", ...DEGREE_OF_SUCCESS_STRINGS] as const) {
    const amount = data.adjustment[outcome];
    if (amount) adjustments[outcome] = { label: rule.label, amount: AMOUNTS[amount]! };
  }
  (state.synthetics.degreeOfSuccessAdjustments[selector] ??= []).push({
    adjustments,
    predicate: resolveInjectedProperties(rule, state, structuredClone(rule.predicate), { warn: false }),
    parentOptions: rule.item.getRollOptions("parent"),
    source: rule.item.uuid,
    sourceName: rule.item.name,
  });
}

export const AdjustDegreeOfSuccessHandler: RuleHandler = { key: "AdjustDegreeOfSuccess", parse, beforePrepareData };
